<template>
  <div class="token-card">
    <div class="logo flex mobile:hidden">
      <img
        :src="token.logo"
        :alt="`${token.name} logo`"
      >
    </div>
      <div class="flex h-full w-full flex-col gap-y-4">
      <div class="flex w-full justify-between gap-x-2 text-primary mobile:justify-start">
        <div class="logo mr-2 hidden mobile:flex">
          <img
            :src="token.logo"
            :alt="`${token.name} logo`"
          >
        </div>
        <div class="flex flex-col gap-y-1">
          <span class="h-fit w-fit rounded-2xl bg-primary/40 px-2 py-0 text-base font-bold text-background">
            {{ token.tokenTypeName }}
          </span>
          <div class="flex flex-wrap items-center gap-x-2 mobile:flex-col mobile:items-start mobile:gap-y-1">
            <strong class="break-all text-[22px] mobile:text-[20px]">{{ token.name }}</strong>
            <span class="h-fit rounded bg-primary/10 px-2 py-1 text-xl mobile:text-lg">{{ token.symbol }}</span>
          </div>
        </div>
        <BcIconLink
          v-show="tokenLinkAddress"
          icon="external-link-square-alt"
          :to="routes.getTokensDetailPageWithTab(tokenLinkAddress)"
          :class="tokenLinkAddress && 'mobile:ml-auto'"
        />
        <BcIconLink
          v-if="token.symbol === NATIVE_TOKEN.symbol"
          icon="external-link-square-alt"
          :to="routes.getTokensDetailPageWithTab(NATIVE_TOKEN.address)"
          :class="'mobile:ml-auto'"
        />
      </div>
      <p class="description">
        {{ token.description }}
      </p>
      <div class="info-container">
        <div class="info">
          <span class="label">Holders</span>
          <p class="flex flex-wrap items-center gap-x-2 font-bold">
            {{ token.holders }}
            <Volatility
              :value="token.holdersChange"
              unit="%"
            >
              <template #prefix>
                <span>(</span>
              </template>
              <template #suffix>
                <span>)</span>
              </template>
            </Volatility>
          </p>
        </div>
        <div v-if="token.symbol === NATIVE_TOKEN.symbol || isUsdt" class="info">
          <div class="flex gap-2">
            <span class="label">Total Supply</span>
            <IconTooltip
              v-if="token.symbol === NATIVE_TOKEN.symbol"
              class="text-primary/40"
              :tooltip="`Total supply is calculated by subtracting the amount used in NXPC Fission from the fixed max supply of 1 billion tokens.
              Tokens on BNB Smart Chain and Avalanche C-Chain are bridged representations of existing supply, not newly minted tokens.`"
            />
          </div>
          
          <p class="break-all font-bold">
            {{ token.totalSupply }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import IconTooltip from '~/components/tooltip/IconTooltip.vue'
import Volatility from '~/components/text/Volatility.vue'
import { TokenCardModel } from '~/types/model/token.model'
import BcIconLink from '~/ui/IconLink/BcIconLink.vue'
import { NATIVE_TOKEN, TETHER_USDT_TOKEN } from '~/constants/token.constants'

interface IProps {
  token: TokenCardModel
}

const props = defineProps<IProps>()

const isUsdt = computed(() => {
  const addr = props.token.address?.toLowerCase()
  return addr === TETHER_USDT_TOKEN.address.toLowerCase()
})

const tokenLinkAddress = computed(() => {
  if (isUsdt.value) return TETHER_USDT_TOKEN.address
  return props.token.address
})
</script>

<style scoped lang="scss">
.token-card {
  @apply flex w-full gap-x-8 rounded-2xl bg-primary/5 p-8 text-primary;

  @include sm {
    @apply flex-col gap-y-4;
  }
}

.logo {
  @apply relative h-24 w-24 shrink-0 items-center justify-center rounded-full overflow-hidden shadow-primary/20 shadow-[0_24px_24px_0];

  &::before {
    @apply absolute top-0 left-0 w-full h-full rounded-full;
    content: '';
    box-shadow: inset 0.5rem 0.5rem 1rem rgba(255, 255, 255, .8),
    inset -0.5rem -0.5rem 1rem rgba(255, 255, 255, .8);
  }

  @include md {
    @apply w-24 h-24 shadow-primary/20 shadow-[0_24px_24px_0];
  }

  @include sm {
    @apply w-16 h-16 shadow-primary/20 shadow-[0_16px_16px_0];
  }

  animation: {
    name: flip;
    duration: 4s;
    iteration-count: infinite;
    timing-function: ease;
  }

  @keyframes flip {
    0% {
      transform: rotateY(0deg);
    }
    50% {
      transform: rotateY(360deg);
    }
    100% {
      transform: rotateY(360deg);
    }
  }

  &:after {
    @apply bg-primary/40 absolute -top-4 -left-[300%] w-[150%] h-2 content-[''] shadow-primary/40 shadow-[0_0_16px_16px];
    animation: {
      name: shine;
      duration: 4s;
      iteration-count: infinite;
      delay: 2s;
    }

    @keyframes shine {
      0% {
        @apply -rotate-45 -left-[300%];
      }
      50% {
        @apply -rotate-45 top-full left-full;
      }
      100% {
        @apply -rotate-45 top-full left-full;
      }
    }
  }
}

.info-container {
  @apply grid grid-cols-2 gap-2;

  @include sm {
    @apply grid-cols-1;
  }

  .info {
    @apply flex flex-col gap-y-1;

    .label {
      @apply text-base text-primary/40;
    }
  }
}

.description {
  @apply text-primary/40 text-lg;
}

</style>
